import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import CustomTextInput from 'modules/keypad/custom-text-input/index.js';
import { debounce, memoize } from 'mojito/utils';

const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { CurrencyHelper } = MojitoPresentation.Utils;
const { TextInput, FlexPane, Checkbox } = MojitoPresentation.Components;
const { isValidNumber, toFixed } = MojitoCore.Base.NumberUtils;

export const REQUEST_BETSLIP_DATA_TIMEOUT = 500;

const CHANGE_TYPE = {
    CHANGE_EVENT: 'CHANGE_EVENT', // Change from onChange event by user.
    BLUR_EVENT: 'BLUR_EVENT', // Change from onBlur event by user.
    OUTSIDE: 'OUTSIDE', // Outside changes from API response.
};

class StakeInput extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.state = {
            stakeString: this.getStakeFromProps(),
            isInFocus: false,
        };

        this.setStake = debounce((stake, isEachWay) => {
            this.props.onStakeChange({ stake, isEachWay });
        }, REQUEST_BETSLIP_DATA_TIMEOUT);

        this.changeType = CHANGE_TYPE.OUTSIDE;
        this.originalInputValue = null;

        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onSelect = this.onSelect.bind(this);
        this.onEachWayToggle = this.onEachWayToggle.bind(this);
        this.getCustomInputConfig = memoize(textInput => ({ textInput }));
    }

    static getDerivedStateFromProps(props) {
        if (!props.showStake) {
            return {
                stakeString: '',
            };
        }

        return null;
    }

    shouldKeepAllDecimals(stake) {
        // Only format stake if the decimals are more than the config amount.
        return +toFixed(stake, this.config.decimalAmount) === stake;
    }

    getStakeFromProps() {
        // If the initial stake is 0, don't show anything.
        return this.props.showStake && this.props.stakeForLine
            ? CurrencyHelper.reformatCurrencyString(
                  this.props.stakeForLine.toString(),
                  this.shouldKeepAllDecimals(this.props.stakeForLine),
                  this.config.decimalAmount
              )
            : '';
    }

    componentDidUpdate(prevProps, prevState) {
        // This is for updating the stake when it was changed by the denominator (column) check
        const hasChangedStake = prevProps.stakeForLine !== this.props.stakeForLine;
        // This is for updating the stake when its validity has changed and is no longer invalid. The stake then needs
        // to be reset to the actual value and no longer show the invalid value.
        const hasChangedValidity = this.props.isValid && !prevProps.isValid;
        // Stake changes are not applied from outside (props) while input is in focus to avoid annoying normalization.
        if (!this.state.isInFocus && (hasChangedStake || hasChangedValidity)) {
            const newStake = this.getStakeFromProps();
            let shouldKeepSeparator = false;
            if (this.originalInputValue) {
                // We change the value of the input only if the separator was filtered and the parsed value stays the same.
                const sameValueWhenParsed =
                    StakeInput.parseStakeString(this.originalInputValue) ===
                    StakeInput.parseStakeString(newStake);

                // We check if the separator is about to be removed from the props and the user didnt remove it manually.
                const separatorGettingRemoved =
                    /^\d+[,.]$/.test(this.state.stakeString) && /^\d+$/.test(newStake);

                shouldKeepSeparator = sameValueWhenParsed && separatorGettingRemoved;
            }

            this.setState({
                stakeString: shouldKeepSeparator ? this.originalInputValue : newStake,
            });
            this.originalInputValue = null;
            return;
        }

        // Trigger API request only if a stake was changed by user.
        if (this.changeType === CHANGE_TYPE.OUTSIDE) {
            return;
        }

        const currentStakeValue = StakeInput.parseStakeString(this.state.stakeString);
        const previousStakeValue = StakeInput.parseStakeString(prevState.stakeString);
        if (currentStakeValue !== previousStakeValue) {
            // If stake was changed due to user input, trigger API request.
            this.requestUpdateStakeData();
        } else if (this.changeType === CHANGE_TYPE.BLUR_EVENT && !this.state.isInFocus) {
            // Else if last state position was BLUR_EVENT and the field is no longer in focus, change state to OUTSIDE
            // to no longer trigger any API requests.
            this.changeType = CHANGE_TYPE.OUTSIDE;
        }
    }

    componentWillUnmount() {
        this.setStake.cancel();
    }

    onFocus() {
        this.props.onFocusCallback();
        this.isStakeNormalized = false;
        this.setState({ isInFocus: true });
    }

    onSelect(stakeString, event) {
        const inputElement = event.target;
        if (this.state.isInFocus) {
            if (!this.isStakeNormalized) {
                const stakeValue = CurrencyHelper.reformatCurrencyString(
                    stakeString,
                    true,
                    this.config.decimalAmount
                );
                this.shouldRestoreCaretPosition =
                    stakeValue.length !== this.state.stakeString.length;
                this.stakeInputSelection = {
                    start: inputElement.selectionStart,
                    end: inputElement.selectionEnd,
                };
                this.isStakeNormalized = true;
                this.updateStakeState(stakeValue, true);
            } else if (this.shouldRestoreCaretPosition) {
                this.shouldRestoreCaretPosition = false;
                inputElement.setSelectionRange(
                    this.stakeInputSelection.start,
                    this.stakeInputSelection.end
                );
            }
        }
    }

    onChange(stakeString, event, isInputFocused) {
        this.changeType = CHANGE_TYPE.CHANGE_EVENT;
        this.updateStakeState(stakeString, isInputFocused, event);
    }

    onBlur(stakeString) {
        this.props.onBlurCallback();
        this.changeType = CHANGE_TYPE.BLUR_EVENT;
        const formattedStakeString = CurrencyHelper.reformatCurrencyString(
            stakeString,
            this.shouldKeepAllDecimals(CurrencyHelper.parseCurrencyString(stakeString)),
            this.config.decimalAmount
        );
        this.updateStakeState(formattedStakeString, false);
    }

    onEachWayToggle() {
        const { isEachWayActivated } = this.props;
        this.props.onStakeChange({
            stake: this.getStakeInputValue(),
            isEachWay: !isEachWayActivated,
        });
        this.emitAnalytics('toggleEachWay', !this.props.isEachWayActivated);
    }

    getStakeInputValue() {
        const { stakeString } = this.state;
        const stake = CurrencyHelper.parseCurrencyString(stakeString);
        return isNaN(stake) ? 0 : stake;
    }

    updateStakeState(stakeString, isInFocus, currentEvent) {
        const inputType = currentEvent?.nativeEvent?.inputType;
        // If stakeString ends with a dot,
        // we store the value to restore it incase it gets filtered out after prop changed
        if (/^\d+[,.]$/.test(stakeString)) {
            this.originalInputValue = stakeString;
        }
        const resetTypes = ['insertFromPaste', 'deleteContentBackward', undefined, ''];
        if (
            (resetTypes.includes(inputType) && isValidNumber(stakeString)) ||
            isValidNumber(stakeString, this.config.decimalAmount)
        ) {
            this.setState({
                stakeString,
                isInFocus,
            });
        } else {
            this.forceUpdate();
        }
    }

    requestUpdateStakeData() {
        this.changeType = CHANGE_TYPE.OUTSIDE;

        // It's important to fix stake value on change,
        // because at the moment when timer it launched the value can be different (from API response)
        const value = this.getStakeInputValue();
        const { isEachWayActivated } = this.props;
        isFinite(value) && this.setStake(value, isEachWayActivated);

        // Make sure all pending set stake actions for particular bet will be aborted.
        this.props.onStakeChangeIntent();
    }

    static parseStakeString(stakeString) {
        return CurrencyHelper.parseCurrencyString(stakeString) || 0;
    }

    renderEachWayCheckbox() {
        if (!this.props.isEachWayAvailable) {
            return null;
        }
        return (
            <Checkbox
                config={this.config.checkbox}
                enabled={!this.props.disabled}
                checked={this.props.isEachWayActivated}
                onClick={this.onEachWayToggle}
                label={this.resolveString('$BETSLIP.EACH_WAY.TOGGLE_BUTTON_LABEL')}
            />
        );
    }

    onKeyEnter(value, event) {
        event.target.blur();
    }

    renderInput() {
        const customInput = this.getCustomInputConfig(this.config.customTextInput);
        const { isEachWayAvailable } = this.props;
        const key = `${isEachWayAvailable}`;
        const stakeValue = this.state.stakeString;
        const commonProps = {
            value: stakeValue,
            disabled: this.props.disabled,
            onChange: this.onChange,
            onBlur: this.onBlur,
            onFocus: this.onFocus,
            hasError: !this.props.isValid,
            class: 'ta-StakeInput',
        };

        if (this.config.inputVisibility.hide) {
            return null;
        }

        if (this.config.useCustomTextInput) {
            return (
                <CustomTextInput
                    {...commonProps}
                    config={customInput}
                    showKeyboard={this.props.showKeyboard}
                />
            );
        }
        return (
            <TextInput
                {...commonProps}
                key={key}
                onSelect={this.onSelect}
                config={this.config.textInput}
                placeholderValue={this.props.placeholderValue}
                onKeyEnter={this.onKeyEnter}
            />
        );
    }

    render() {
        if (this.props.hidden || this.config.hide) {
            return null;
        }
        const { container, eachWayContainer } = this.style;
        const { isEachWayAvailable } = this.props;
        return (
            <FlexPane
                key={this.props.isEachWayAvailable}
                config={isEachWayAvailable ? eachWayContainer : container}
            >
                {this.renderEachWayCheckbox()}
                {this.renderInput()}
            </FlexPane>
        );
    }
}

StakeInput.getStyle = (config, applicationMode, merge) => {
    const container = config.container;
    return {
        container: merge(container, {
            style: {
                flexDirection: 'column',
            },
        }),
        eachWayContainer: container,
    };
};

export default StakeInput;
